import type { TFunction } from 'i18next';

import type { WorkflowSummary, WorkflowType } from '@/api/workflows';

/**
 * Renders the workflow's assignment-type as a localized label.
 * Centralised so the three pages that show it (Workflows card, Workflow Details,
 * Approver Review) can't drift apart when a new type is added.
 */
export function assigneeLabel(type: WorkflowType, t: TFunction): string {
  switch (type) {
    case 'InternalAdvisor':
      return t('workflow.types.internal');
    case 'ExternalAdvisor':
      return t('workflow.types.external');
    case 'Both':
      return t('workflow.types.both');
  }
}

/** Convenience: read it straight off a template. */
export const assigneeOf = (w: WorkflowSummary, t: TFunction): string => assigneeLabel(w.type, t);

/**
 * Pick the French or English version of a workflow's name / description based on the
 * current i18n language. Falls back to the English value when the French one is empty
 * (matches the existing inline behaviour across pages).
 */
export function localizedName(w: WorkflowSummary, isFr: boolean): string {
  return (isFr ? w.nameFr : w.nameEn) || w.nameEn;
}

export function localizedDescription(w: WorkflowSummary, isFr: boolean): string {
  return (isFr ? w.descriptionFr : w.descriptionEn) || w.descriptionEn;
}

/**
 * The exact string a workflow card puts in its title — the localized name, or
 * the app name when the workflow has no name in the active language.
 *
 * Anything that sorts, searches or labels by "the workflow's name" must call
 * this rather than reproducing the fallback chain, or it ends up keyed on a
 * value the user cannot see. That is not hypothetical: sorting used to fall
 * back `nameEn -> nameFr -> appName` while the card fell back
 * `nameEn -> appName`, so an English reader looking at a workflow named only in
 * French saw cards ordered by a name that appeared nowhere on screen.
 */
export function workflowCardTitle(w: WorkflowSummary, isFr: boolean): string {
  return localizedName(w, isFr) || w.appName;
}

/**
 * A → Z comparator for workflow cards.
 *
 * `numeric` is what makes this match what a reader means by alphabetical:
 * without it the collator compares digits as characters, so `Test 10` lands
 * between `Test 1` and `Test 2` — the whole list reads as unsorted once the
 * names carry numbers, which on a QA dashboard they almost always do.
 *
 * `sensitivity: 'base'` keeps `apple` next to `Apple` instead of banishing all
 * lowercase names to the end, but it also makes those two compare EQUAL, and
 * equal elements just keep whatever order the API returned. The two tiebreaks
 * below make the order total so the same data always renders the same way.
 */
export function compareWorkflowsByName(
  a: WorkflowSummary,
  b: WorkflowSummary,
  language: string,
  isFr: boolean,
): number {
  const options: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' };
  const aTitle = workflowCardTitle(a, isFr);
  const bTitle = workflowCardTitle(b, isFr);
  const byTitle = aTitle.localeCompare(bTitle, language, options);
  if (byTitle !== 0) return byTitle;
  // Same name ignoring case/accents — order by the full comparison so `Test`
  // and `test` at least land in a stable, repeatable order.
  const byExactTitle = aTitle.localeCompare(bTitle, language, { numeric: true });
  if (byExactTitle !== 0) return byExactTitle;
  // Genuinely identical titles: app names are unique, so this always decides.
  return a.appName.localeCompare(b.appName, language, options);
}
